import { RefObject, useEffect } from 'react';
import { PlayerRef } from '@remotion/player';
import useAnalytics from '@/hooks/useAnalytics';
import { roundToNDecimalPlaces } from '@/libs/utils';

export function useTrackPlayerEvents({
  playerRef,
  fps,
  clipId
}: {
  playerRef: RefObject<PlayerRef>;
  fps: number;
  clipId: string;
}) {
  const { trackPlayerPlay, trackPlayerPause, trackPlayerSeek } = useAnalytics();

  useEffect(() => {
    const player = playerRef.current;
    if (player) {
      const handleSeek = ({ detail: { frame } }: { detail: { frame: number } }) => {
        const seekTime = roundToNDecimalPlaces(frame / fps, 3);

        trackPlayerSeek({
          clipId,
          seekTime
        });
      };

      const handlePlay = () => {
        trackPlayerPlay({
          clipId,
          currentTime: roundToNDecimalPlaces(player.getCurrentFrame() / fps, 3)
        });
      };

      const handlePause = () => {
        trackPlayerPause({
          clipId,
          currentTime: roundToNDecimalPlaces(player.getCurrentFrame() / fps, 3)
        });
      };

      player.addEventListener('seeked', handleSeek);
      player.addEventListener('play', handlePlay);
      player.addEventListener('pause', handlePause);

      return () => {
        player.removeEventListener('seeked', handleSeek);
        player.removeEventListener('play', handlePlay);
        player.removeEventListener('pause', handlePause);
      };
    }
  }, [playerRef, trackPlayerSeek, trackPlayerPlay, trackPlayerPause, fps, clipId]);
}
